import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Card,
  Label,
  CardBody,
  CardHeader,
} from "reactstrap";
import Select from "react-select";
import NumberCommas from "../../Components/NumberCommas/NumberCommas";
import { formatDate } from "../../Components/FormatDate/FormatDate";
import LayoutWithBreadCrumb from "../../pages/Pages/Starter/LayoutWithBreadCrumb";
import BankServices from "../../Services/QRCodeServices/BankServices/BankServices";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";

const BankStatement = () => {
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bankData, setBankData] = useState({
    bankId: 0,
    bankName: "",
    currentBalance: 0,
    transactions: [],
  });
  const [isPrinting, setIsPrinting] = useState(false);

  const bankOptions = banks.map((bank) => ({
    value: bank.id,
    label: bank.name,
  }));

  const handleBankChange = (selectedOption) => {
    setSelectedBankId(selectedOption ? selectedOption.value : "");
  };

  async function fetchBankTransactions(id, start, end) {
    try {
      const response = await BankServices.getBankStatement(id, start, end);
      setBankData(response);
    } catch (error) {
      console.error("Error fetching bank statement:", error);
    }
  }

  useEffect(() => {
    async function fetchBanks() {
      try {
        const response = await BankServices.getList();
        setBanks(response);
      } catch (error) {
        console.error("Error fetching bank list:", error);
      }
    }

    fetchBanks();
  }, []);

  const handleReportClick = () => {
    if (selectedBankId) {
      fetchBankTransactions(selectedBankId, startDate || null, endDate || null);
    }
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  return (
    <React.Fragment>
      <LayoutWithBreadCrumb
        title="Bank Statement Report"
        pageTitle="Bank Statement Report"
      >
        <Card>
          <CardBody>
            <PrintHeader reportTitle="Bank Statement Report" />
            <Form className="d-print-none">
              <Row className="mb-3 align-items-end">
                <Col sm={3}>
                  <Label>Bank Selection</Label>
                  <Select
                    value={bankOptions.find(
                      (option) => option.value === selectedBankId
                    )}
                    onChange={handleBankChange}
                    options={bankOptions}
                    isClearable
                  />
                </Col>
                <Col sm={3}>
                  <Label>Start Date</Label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Col>
                <Col sm={3}>
                  <Label>End Date</Label>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </Col>
                <Col sm={3} className="text-end">
                  <Button
                    onClick={handleReportClick}
                    className="me-2"
                    disabled={!selectedBankId}
                  >
                    Generate Report
                  </Button>
                  {bankData.bankId > 0 && (
                    <Button
                      color="outline-secondary"
                      className="py-2 px-3 d-inline-flex align-items-center shadow-sm"
                      onClick={handlePrint}
                    >
                      <i className="ri-printer-line fs-16 me-1"></i>
                    </Button>
                  )}
                </Col>
              </Row>
            </Form>

            <Row>
              {bankData.bankId > 0 && (
                <Card className="shadow-sm border-0">
                  <CardBody className="p-4">
                    <h5 className="mb-4 text-center font-weight-bold d-print-none">
                      Bank Statement Information
                    </h5>
                    <Row className="mb-4">
                      <Col sm={12} lg={6} className="mb-2 mb-lg-0">
                        <div className="d-flex mb-2">
                          <span className="font-weight-bold text-muted col-4 col-sm-3">
                            Bank Name:
                          </span>
                          <span className="font-weight-bold col-8 col-sm-9">
                            {bankData.bankName}
                          </span>
                        </div>
                      </Col>

                      <Col sm={12} lg={6} className="mb-2 mb-lg-0">
                        <div className="d-flex mb-2">
                          <span className="font-weight-bold text-muted col-4 col-sm-3">
                            Current Balance:
                          </span>
                          <span className="font-weight-bold col-8 col-sm-9">
                            Rs.
                            <NumberCommas number={bankData.currentBalance} />
                          </span>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table bordered hover className="mb-0 table-nowrap">
                        <thead className="bg-light">
                          <tr>
                            <th style={{ width: "60px" }}>S.N</th>
                            <th style={{ width: "180px" }}>Transaction ID</th>
                            <th style={{ width: "130px" }}>Date</th>
                            <th>Remarks</th>
                            <th style={{ width: "120px" }}>Debit</th>
                            <th style={{ width: "120px" }}>Credit</th>
                            <th style={{ width: "150px" }}>Transaction By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankData.transactions &&
                          bankData.transactions.length > 0 ? (
                            bankData.transactions.map((item, index) => (
                              <tr key={item.transactionId || index}>
                                <td>{index + 1}</td>
                                <td>{item.transactionId}</td>
                                <td>{formatDate(item.valueDate)}</td>
                                <td>{item.remarks}</td>
                                <td>
                                  {item.transactionType === "Debit" ? (
                                    <NumberCommas number={item.amount} />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {item.transactionType === "Credit" ? (
                                    <NumberCommas number={item.amount} />
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>{item.transactionCreatedBy}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No transactions available.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot className="font-weight-bold">
                          <tr>
                            <td colSpan="4" className="text-end">
                              Total
                            </td>
                            <td>
                              <NumberCommas
                                number={bankData.transactions
                                  .filter((t) => t.transactionType === "Debit")
                                  .reduce((sum, t) => sum + t.amount, 0)}
                              />
                            </td>
                            <td>
                              <NumberCommas
                                number={bankData.transactions
                                  .filter((t) => t.transactionType === "Credit")
                                  .reduce((sum, t) => sum + t.amount, 0)}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Row>
          </CardBody>
        </Card>
      </LayoutWithBreadCrumb>
    </React.Fragment>
  );
};

export default BankStatement;